import React, { useEffect, useRef, useState } from 'react';
import { getArtworkPath } from '../utils/slugify';

const ArtworkPreview = ({ 
  artwork, 
  nextArtwork, 
  prevArtwork, 
  isOpen, 
  onClose, 
  language, 
  clickPosition, 
  onNavigate, 
  isFirstItem, 
  isLastItem 
}) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [transitionDirection, setTransitionDirection] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentArtwork, setCurrentArtwork] = useState(artwork);
  const [artworkId, setArtworkId] = useState(null);
  
  // Track artwork changes for transitions
  useEffect(() => {
    if (artwork && currentArtwork && artwork.id !== currentArtwork.id) {
      setIsTransitioning(true);
      setCurrentArtwork(artwork);
      
      // Reset transition state after animation completes
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300); // Match this duration with CSS transition time
      
      return () => clearTimeout(timer);
    } else if (artwork && !currentArtwork) {
      setCurrentArtwork(artwork);
    }
  }, [artwork, currentArtwork]);
  
  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      setImageDimensions({ width: naturalWidth, height: naturalHeight });
    }
  };
  
  useEffect(() => {
    if (isOpen && clickPosition && contentRef.current && imageRef.current && overlayRef.current) {
      document.body.style.overflow = 'hidden';
      
      const startX = clickPosition.x;
      const startY = clickPosition.y;
      const startWidth = clickPosition.width;
      
      const modalRect = contentRef.current.getBoundingClientRect();
      const imageRect = imageRef.current.getBoundingClientRect();
      
      const scaleX = startWidth / imageRect.width;
      const translateX = startX - (modalRect.left + (imageRect.left - modalRect.left));
      const translateY = startY - (modalRect.top + (imageRect.top - modalRect.top));
      
      contentRef.current.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX})`;
      contentRef.current.style.transformOrigin = 'top left';
      contentRef.current.style.opacity = '1';
      
      overlayRef.current.style.backgroundColor = 'rgba(0, 0, 0, 0)';
      
      requestAnimationFrame(() => {
        overlayRef.current.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        contentRef.current.style.transform = 'translate(0, 0) scale(1)';
      });
    } else if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, clickPosition]);

  // Fix transitions by tracking artwork changes with useEffect
  useEffect(() => {
    if (isOpen && artwork) {
      // When a new image is selected, trigger the transition
      if (transitionDirection) {
        setIsTransitioning(true);
        
        // Reset the transition state after animation completes
        const timer = setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
        
        return () => clearTimeout(timer);
      }
    }
  }, [artwork, isOpen, transitionDirection]);

  // Reset transition direction when closing the preview
  useEffect(() => {
    if (!isOpen) {
      setTransitionDirection(null);
    }
  }, [isOpen]);

  // Track the artwork ID to detect changes
  useEffect(() => {
    if (isOpen && artwork) {
      // If artwork has changed and we have a transition direction
      if (artworkId !== artwork.id && transitionDirection) {
        // Start transition
        setIsTransitioning(true);
        
        // Set timeout to end transition
        const timer = setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
        
        // Update the artwork ID we're tracking
        setArtworkId(artwork.id);
        
        return () => clearTimeout(timer);
      } else if (artworkId !== artwork.id) {
        // Just update the artwork ID if no transition direction
        setArtworkId(artwork.id);
      }
    }
  }, [artwork, isOpen, transitionDirection, artworkId]);

  // Add history management for preview with clean URLs
  useEffect(() => {
    if (isOpen && artwork) {
      // Generate clean URL for this artwork
      const artworkPath = getArtworkPath(artwork, language);
      const currentPath = window.location.pathname;
      
      // Only push history if we're not already on an artwork URL
      // (i.e., user clicked from gallery, not direct link)
      const isAlreadyOnArtworkUrl = currentPath.startsWith('/werk/') || currentPath.startsWith('/artwork/');
      
      if (!isAlreadyOnArtworkUrl) {
        // Push a history entry when opening preview from gallery
        window.history.pushState({ action: 'preview', artworkId: artwork?.id }, '', artworkPath);
      } else {
        // Just update the URL without adding to history (for direct links)
        window.history.replaceState({ action: 'preview', artworkId: artwork?.id }, '', artworkPath);
      }
      
      // Handle back button to close preview
      const handlePopState = () => {
        onClose();
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isOpen, artwork, onClose, language]);

  // Update URL when navigating between artworks (without adding to history stack)
  useEffect(() => {
    if (isOpen && currentArtwork && artworkId === currentArtwork.id) {
      const artworkPath = getArtworkPath(currentArtwork, language);
      window.history.replaceState({ action: 'preview', artworkId: currentArtwork.id }, '', artworkPath);
    }
  }, [currentArtwork, artworkId, isOpen, language]);

  // Preload adjacent images for instant navigation
  useEffect(() => {
    if (!isOpen) return;

    const preloadedImages = [];
    const linkElements = [];

    // Preload next image
    if (nextArtwork && nextArtwork.image) {
      // Method 1: Create Image object for immediate fetch
      const nextImg = new Image();
      nextImg.src = `/images/${nextArtwork.image}`;
      preloadedImages.push(nextImg);

      // Method 2: Add prefetch link to head for browser optimization
      const nextLink = document.createElement('link');
      nextLink.rel = 'prefetch';
      nextLink.as = 'image';
      nextLink.href = `/images/${nextArtwork.image}`;
      document.head.appendChild(nextLink);
      linkElements.push(nextLink);
    }

    // Preload previous image
    if (prevArtwork && prevArtwork.image) {
      // Method 1: Create Image object for immediate fetch
      const prevImg = new Image();
      prevImg.src = `/images/${prevArtwork.image}`;
      preloadedImages.push(prevImg);

      // Method 2: Add prefetch link to head for browser optimization
      const prevLink = document.createElement('link');
      prevLink.rel = 'prefetch';
      prevLink.as = 'image';
      prevLink.href = `/images/${prevArtwork.image}`;
      document.head.appendChild(prevLink);
      linkElements.push(prevLink);
    }

    // Cleanup function - remove prefetch links
    return () => {
      linkElements.forEach(link => {
        if (link.parentNode) {
          document.head.removeChild(link);
        }
      });
      // Images remain cached in browser
    };
  }, [isOpen, nextArtwork, prevArtwork]);

  if (!isOpen) return null;
  
  const handlePrevious = (e) => {
    e.stopPropagation();
    setTransitionDirection('prev');
    onNavigate('prev');
  };
  
  const handleNext = (e) => {
    e.stopPropagation();
    setTransitionDirection('next');
    onNavigate('next');
  };
  
  return (
    <div 
      ref={overlayRef}
      className="artwork-preview-overlay" 
      onClick={onClose}
    >
      <div 
        ref={contentRef} 
        className="artwork-preview-content" 
        onClick={e => e.stopPropagation()}
        style={{
          width: imageDimensions.width > 0 ? 
            `${Math.min(imageDimensions.width, window.innerWidth * 0.9)}px` : 
            'auto'
        }}
      >
        <button className="artwork-preview-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        
        <div className="artwork-preview-image-container">
          <div className={`artwork-image-wrapper ${isTransitioning ? `transitioning ${transitionDirection}` : ''}`}>
            <img 
              ref={imageRef} 
              src={`/images/${currentArtwork?.image}`} 
              alt={`${language === 'en' ? currentArtwork?.title.en : currentArtwork?.title.de} - ${language === 'en' ? currentArtwork?.medium.en : currentArtwork?.medium.de} ${language === 'en' ? 'by' : 'von'} Waltraud Pfister-Bläske`}
              className="artwork-preview-image"
              onLoad={handleImageLoad}
            />
          </div>
          
          {!isFirstItem && (
            <button className="artwork-preview-nav prev" onClick={handlePrevious} aria-label="Previous">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {!isLastItem && (
            <button className="artwork-preview-nav next" onClick={handleNext} aria-label="Next">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 5L19 12L12 19" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        
        <div className={`artwork-preview-info ${isTransitioning ? 'fade' : ''}`}>
          <h2>{language === 'en' ? currentArtwork?.title.en : currentArtwork?.title.de}</h2>
          <p className="artwork-preview-medium">
            {language === 'en' ? currentArtwork?.medium.en : currentArtwork?.medium.de}
          </p>
          {currentArtwork?.description && (
            <p className="artwork-preview-description">
              {language === 'en' ? currentArtwork.description.en : currentArtwork.description.de}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtworkPreview;
